import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { FiPlus, FiSearch } from "react-icons/fi";
import { normalize } from "../Catalogue/catalogueData";
import { whatsappLink } from "../../contact";
import "./Faqs.css";

const faqs = [
  {
    question: "Qu'est-ce que Cristal Inox ?",
    answer:
      "Cristal Inox est une entreprise spécialisée dans la chaudronnerie inoxydable. Nous proposons des services de fabrication et de transformation d'inox pour divers secteurs comme la décoration, le médical et le tuyautage industriel. Notre mission est d'offrir des solutions sur mesure avec une qualité irréprochable, adaptées aux besoins spécifiques de nos clients.",
  },
  {
    question: "Quels types de produits fabriquez-vous ?",
    answer:
      "Nous fabriquons une large gamme de produits en inox, allant des équipements industriels aux éléments de décoration. Cela inclut des garde-corps, des plans de travail, des cuves, des structures métalliques et bien plus encore. Chaque pièce est conçue avec précision et souci du détail pour garantir une finition impeccable.",
  },
  {
    question: "Pourquoi choisir l'inox pour vos projets ?",
    answer:
      "L'inox est un matériau exceptionnel grâce à sa résistance à la corrosion, sa durabilité et son esthétique moderne. Il est idéal pour des applications dans des environnements exigeants comme l'industrie alimentaire, la médecine ou l'architecture. Chez Cristal Inox, nous utilisons de l'inox de haute qualité pour assurer la longévité et la fiabilité de nos réalisations.",
  },
  {
    question: "Proposez-vous des services sur mesure ?",
    answer:
      "Oui, nous sommes spécialisés dans la fabrication sur mesure. Nous travaillons en étroite collaboration avec nos clients pour concevoir et réaliser des pièces uniques qui répondent parfaitement à leurs besoins. Que ce soit pour un projet architectural, industriel ou domestique, nous nous adaptons à vos exigences avec professionnalisme et expertise.",
  },
  {
    question: "Comment demander un devis ?",
    answer:
      "Vous pouvez nous contacter directement via notre site web, par téléphone ou par e-mail. Nous étudierons votre demande avec attention et vous proposerons une solution adaptée à votre budget et à vos exigences. Notre équipe est toujours disponible pour vous conseiller et vous accompagner dans la réalisation de votre projet.",
  },
  {
    question: "Quels sont vos délais de fabrication ?",
    answer:
      "Nos délais varient en fonction de la complexité du projet et de la charge de travail en cours. En général, un projet standard peut être réalisé en quelques semaines. Nous nous engageons à respecter des délais raisonnables tout en garantissant un travail de haute qualité. Pour obtenir une estimation précise, contactez-nous avec les détails de votre projet.",
  },
  {
    question: "Travaillez-vous avec des professionnels et des particuliers ?",
    answer:
      "Oui, nous collaborons aussi bien avec des entreprises qu'avec des particuliers. Que vous soyez un architecte, un industriel ou un particulier souhaitant un aménagement en inox sur mesure, nous avons l'expertise nécessaire pour répondre à vos attentes. Notre équipe est à votre écoute pour vous conseiller et concrétiser votre projet.",
  },
];

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const visible = useMemo(() => {
    const query = normalize(searchTerm);
    return faqs.filter(
      (faq) =>
        normalize(faq.question).includes(query) ||
        normalize(faq.answer).includes(query)
    );
  }, [searchTerm]);

  return (
    <div className="fq">
      <header className="fq-hero">
        <motion.span
          className="fq-eyebrow"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          FAQ
        </motion.span>
        <motion.h1
          className="fq-title"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
        >
          Questions fréquentes
        </motion.h1>
        <motion.label
          className="fq-search"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
        >
          <FiSearch aria-hidden="true" />
          <input
            type="search"
            placeholder="Rechercher une question..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setOpenIndex(null);
            }}
          />
        </motion.label>
      </header>

      <main className="fq-main">
        <div className="fq-list">
          {visible.map((faq, index) => {
            const open = openIndex === index;
            return (
              <motion.div
                key={faq.question}
                className={`fq-item${open ? " is-open" : ""}`}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: Math.min(index, 6) * 0.06 }}
              >
                <h2>
                  <button
                    type="button"
                    className="fq-question"
                    aria-expanded={open}
                    aria-controls={`fq-answer-${index}`}
                    onClick={() => setOpenIndex(open ? null : index)}
                  >
                    <span>{faq.question}</span>
                    <FiPlus className="fq-icon" aria-hidden="true" />
                  </button>
                </h2>
                <AnimatePresence initial={false}>
                  {open && (
                    <motion.div
                      id={`fq-answer-${index}`}
                      className="fq-answer"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                    >
                      <p>{faq.answer}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
          {visible.length === 0 && (
            <p className="fq-empty">Aucune question ne correspond à « {searchTerm} ».</p>
          )}
        </div>

        <div className="fq-cta">
          <h2>Vous ne trouvez pas votre réponse ?</h2>
          <p>Écrivez-nous, nous vous répondons rapidement.</p>
          <div className="fq-cta__buttons">
            <Link to="/contact" className="fq-btn">
              Nous contacter
            </Link>
            <a
              href={whatsappLink("Bonjour, j'ai une question sur vos produits en inox.")}
              target="_blank"
              rel="noopener noreferrer"
              className="fq-btn fq-btn--whatsapp"
            >
              WhatsApp
            </a>
          </div>
        </div>
      </main>
    </div>
  );
};

export default FAQ;
